import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAdminBooks, useGenerateAllBookContent, useGenerateBookContent } from '@/hooks/use-admin'
import type { AdminBook } from '@/services/admin.service'
import { BookOpen, CheckCircle, Clock, Loader2, Play, Zap } from 'lucide-react'
import { useState } from 'react'

const CATEGORY_LABELS: Record<string, string> = {
  career_development: 'Career Development',
  leadership: 'Leadership',
  communication: 'Communication',
  productivity: 'Productivity',
  technical: 'Technical',
  entrepreneurship: 'Entrepreneurship',
  personal_growth: 'Personal Growth',
  job_search: 'Job Search',
  networking: 'Networking',
  finance: 'Finance',
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
        Not generated
      </span>
    )
  }

  switch (status) {
    case 'pending':
    case 'generating':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
          <Clock className="h-3 w-3" />
          {status === 'pending' ? 'Pending' : 'Generating'}
        </span>
      )
    case 'completed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
          <CheckCircle className="h-3 w-3" />
          Completed
        </span>
      )
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
          Failed
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {status}
        </span>
      )
  }
}

function BookRow({
  book,
  onGenerate,
  isGenerating,
}: {
  book: AdminBook
  onGenerate: (bookId: number) => void
  isGenerating: boolean
}) {
  const canGenerate =
    !book.generation_status ||
    book.generation_status === 'failed' ||
    book.generation_status === 'pending' ||
    book.generation_status === 'generating'

  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {book.cover_url ? (
            <img
              src={book.cover_url}
              alt={book.title}
              className="h-12 w-8 rounded object-cover bg-muted"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                target.nextElementSibling?.classList.remove('hidden')
              }}
            />
          ) : null}
          <div
            className={`h-12 w-8 rounded bg-muted flex items-center justify-center ${book.cover_url ? 'hidden' : ''}`}
          >
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <div className="font-medium text-sm">{book.title}</div>
            <div className="text-xs text-muted-foreground">{book.author}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {CATEGORY_LABELS[book.category] || book.category}
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {book.rating ? book.rating.toFixed(1) : '-'}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={book.generation_status} />
      </td>
      <td className="px-4 py-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onGenerate(book.id)}
          disabled={!canGenerate || isGenerating}
        >
          {isGenerating ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <Play className="h-3 w-3 mr-1" />
          )}
          {book.generation_status && book.generation_status !== 'completed' ? 'Retry' : 'Generate'}
        </Button>
      </td>
    </tr>
  )
}

export function AdminBooksPage() {
  const { data, isLoading, refetch } = useAdminBooks()
  const generateBook = useGenerateBookContent()
  const generateAll = useGenerateAllBookContent()
  const [showGenerateAllDialog, setShowGenerateAllDialog] = useState(false)
  const [generatingBookId, setGeneratingBookId] = useState<number | null>(null)
  const [lastResult, setLastResult] = useState<string | null>(null)

  const handleGenerate = async (bookId: number) => {
    setGeneratingBookId(bookId)
    try {
      const result = await generateBook.mutateAsync(bookId)
      setLastResult(`Queued: ${result.message}`)
    } catch (err) {
      setLastResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setGeneratingBookId(null)
    }
  }

  const handleGenerateAll = async () => {
    try {
      const result = await generateAll.mutateAsync()
      setLastResult(result.message)
      setShowGenerateAllDialog(false)
    } catch (err) {
      setLastResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
      setShowGenerateAllDialog(false)
    }
  }

  const books = data?.books || []
  const generated = books.filter((b) => b.generation_status === 'completed').length
  const pending = books.filter(
    (b) => b.generation_status === 'pending' || b.generation_status === 'generating',
  ).length
  const notGenerated = books.filter((b) => !b.generation_status).length

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Book Content</h1>
          <p className="text-muted-foreground">Manage AI-generated content for the book catalog</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
            Refresh
          </Button>
          <Button onClick={() => setShowGenerateAllDialog(true)} disabled={generateAll.isPending}>
            <Zap className="h-4 w-4 mr-1" />
            Generate All
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Books</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{books.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Generated</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{generated}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Not Generated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{notGenerated}</div>
          </CardContent>
        </Card>
      </div>

      {/* Status message */}
      {lastResult && (
        <div className="rounded-md bg-blue-50 px-4 py-3 text-sm text-blue-700">
          {lastResult}
          <button
            type="button"
            className="ml-2 text-blue-500 hover:text-blue-800"
            onClick={() => setLastResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Book table */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Book
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Rating
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {books.map((book) => (
                  <BookRow
                    key={book.id}
                    book={book}
                    onGenerate={handleGenerate}
                    isGenerating={generatingBookId === book.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Generate All Confirmation */}
      <Dialog open={showGenerateAllDialog} onOpenChange={setShowGenerateAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate All Book Content</DialogTitle>
            <DialogDescription>
              This will queue content generation for {notGenerated} books that haven't been
              generated yet. Each book will get key takeaways, chapter summaries, flashcards, and
              action items generated by AI. Books that already have content will be skipped.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateAllDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateAll} disabled={generateAll.isPending}>
              {generateAll.isPending ? 'Queuing...' : `Generate ${notGenerated} Books`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
